// services/forecast/forecastservices.js
//
// Service layer for the collection forecasting system. Same shape as
// services/pbi/pbiservices.js:
//   - Thin axios wrappers via apiClient
//   - One named export per endpoint
//   - URLs from FORECAST_URLS (apiConfig.js) — full URLs including
//     ${BASE_URL}, just like PBI_URLS
//   - Errors logged with status + data, then re-thrown for component
//     error handling

import { FORECAST_URLS } from "../apiConfig.js";
import apiClient from "../apiInstance.js";

// ── Helpers ────────────────────────────────────────────────────────
async function get(url, params) {
    try {
        const response = await apiClient.get(url, { params });
        return response.data;
    } catch (err) {
        console.error(`Error fetching ${url}`,
            err?.response?.status, err?.response?.data || err?.message);
        throw err;
    }
}

async function post(url, body) {
    try {
        const response = await apiClient.post(url, body);
        return response.data;
    } catch (err) {
        console.error(`Error posting ${url}`,
            err?.response?.status, err?.response?.data || err?.message);
        throw err;
    }
}

async function put(url, body) {
    try {
        const response = await apiClient.put(url, body);
        return response.data;
    } catch (err) {
        console.error(`Error putting ${url}`,
            err?.response?.status, err?.response?.data || err?.message);
        throw err;
    }
}

async function del(url) {
    try {
        const response = await apiClient.delete(url);
        return response.data;
    } catch (err) {
        console.error(`Error deleting ${url}`,
            err?.response?.status, err?.response?.data || err?.message);
        throw err;
    }
}


// ═════════════════════════════════════════════════════════════════
//   MODELS
// ═════════════════════════════════════════════════════════════════
export const getForecastModels        = ()           => get (FORECAST_URLS.FORECAST_MODELS);
export const getForecastModel         = (id)         => get (`${FORECAST_URLS.FORECAST_MODELS}/${id}`);
export const createForecastModel      = (payload)    => post(FORECAST_URLS.FORECAST_MODELS, payload);
export const updateForecastModel      = (id, body)   => put (`${FORECAST_URLS.FORECAST_MODELS}/${id}`, body);
export const deleteForecastModel      = (id)         => del (`${FORECAST_URLS.FORECAST_MODELS}/${id}`);
export const duplicateForecastModel   = (id, body)   => post(`${FORECAST_URLS.FORECAST_MODELS}/${id}/duplicate`, body);


// ═════════════════════════════════════════════════════════════════
//   CHANNEL PERSONAS
// ═════════════════════════════════════════════════════════════════
export const getChannelPersonas       = (modelId)         => get (`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/channel-personas`);
export const createChannelPersona     = (modelId, body)   => post(`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/channel-personas`, body);
export const updateChannelPersona     = (id, body)        => put (`${FORECAST_URLS.FORECAST_CHANNEL_PERSONAS}/${id}`, body);
export const deleteChannelPersona     = (id)              => del (`${FORECAST_URLS.FORECAST_CHANNEL_PERSONAS}/${id}`);


// ═════════════════════════════════════════════════════════════════
//   BEHAVIORAL PERSONAS
// ═════════════════════════════════════════════════════════════════
export const getBehavioralPersonas    = (modelId)         => get (`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/behavioral-personas`);
export const createBehavioralPersona  = (modelId, body)   => post(`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/behavioral-personas`, body);
export const updateBehavioralPersona  = (id, body)        => put (`${FORECAST_URLS.FORECAST_BEHAVIORAL_PERSONAS}/${id}`, body);
export const deleteBehavioralPersona  = (id)              => del (`${FORECAST_URLS.FORECAST_BEHAVIORAL_PERSONAS}/${id}`);


// ═════════════════════════════════════════════════════════════════
//   HOLIDAYS
// ═════════════════════════════════════════════════════════════════
export const getForecastHolidays      = ()         => get (FORECAST_URLS.FORECAST_HOLIDAYS);
export const getForecastHoliday       = (id)       => get (`${FORECAST_URLS.FORECAST_HOLIDAYS}/${id}`);
export const createHoliday            = (body)     => post(FORECAST_URLS.FORECAST_HOLIDAYS, body);
export const updateHoliday            = (id, body) => put (`${FORECAST_URLS.FORECAST_HOLIDAYS}/${id}`, body);
export const deleteHoliday            = (id)       => del (`${FORECAST_URLS.FORECAST_HOLIDAYS}/${id}`);


// ═════════════════════════════════════════════════════════════════
//   CALENDAR
// ═════════════════════════════════════════════════════════════════
export const getCalendarDays          = (params)   => get (FORECAST_URLS.FORECAST_CALENDAR, params);
export const regenerateCalendar       = ()         => post(FORECAST_URLS.FORECAST_CALENDAR_REGEN);


// ═════════════════════════════════════════════════════════════════
//   BASE CURVES
// ═════════════════════════════════════════════════════════════════
export const getBaseCurves            = (modelId)         => get(`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/base-curves`);
export const saveBaseCurves           = (modelId, body)   => put(`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/base-curves`, body);


// ═════════════════════════════════════════════════════════════════
//   FEATURE MODIFIERS
// ═════════════════════════════════════════════════════════════════
export const getFeatureModifiers      = (modelId)         => get(`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/feature-modifiers`);
export const saveFeatureModifiers     = (modelId, body)   => put(`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/feature-modifiers`, body);


// ═════════════════════════════════════════════════════════════════
//   FUNNEL PARAMS
// ═════════════════════════════════════════════════════════════════
export const getFunnelParams          = (modelId)         => get(`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/funnel-params`);
export const saveFunnelParams         = (modelId, body)   => put(`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/funnel-params`, body);


// ═════════════════════════════════════════════════════════════════
//   MODEL BASELINES
// ═════════════════════════════════════════════════════════════════
export const getChannelDayShares     = (modelId)         => get(`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/channel-day-shares`);
export const saveChannelDayShares    = (modelId, body)   => put(`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/channel-day-shares`, body);
export const getModelBaseline        = (modelId)         => get(`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/baseline`);
export const saveModelBaseline       = (modelId, body)   => put(`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/baseline`, body);


// ═════════════════════════════════════════════════════════════════
//   REFERENCE TABLES (read-only)
// ═════════════════════════════════════════════════════════════════
export const getDpdBuckets            = () => get(FORECAST_URLS.FORECAST_DPD_BUCKETS);
export const getPtpOutBuckets         = () => get(FORECAST_URLS.FORECAST_PTP_OUT_BUCKETS);
export const getConversionTypes       = () => get(FORECAST_URLS.FORECAST_CONVERSION_TYPES);
export const getFeatures              = () => get(FORECAST_URLS.FORECAST_FEATURES);


// ═════════════════════════════════════════════════════════════════
//   FORECAST RUNS
// ═════════════════════════════════════════════════════════════════
export const getModelRuns             = (modelId)             => get (`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/runs`);
export const runForecast              = (modelId, body)       => post(`${FORECAST_URLS.FORECAST_MODELS}/${modelId}/runs`, body);
export const getForecastRun           = (runId)               => get (`${FORECAST_URLS.FORECAST_RUNS}/${runId}`);
export const getForecastDaily         = (runId, params)       => get (`${FORECAST_URLS.FORECAST_RUNS}/${runId}/daily`, params);
export const deleteForecastRun        = (runId)               => del (`${FORECAST_URLS.FORECAST_RUNS}/${runId}`);
export const saveChannelDayAttempts = (modelId, body) =>
    put(FORECAST_URLS.FORECAST_CHANNEL_DAY_ATTEMPTS(modelId), body);


// ═════════════════════════════════════════════════════════════════
//   INGEST
// ═════════════════════════════════════════════════════════════════
export const getIngestBatches         = ()       => get (FORECAST_URLS.FORECAST_INGEST_BATCHES);
export const getIngestBatch           = (id)     => get (`${FORECAST_URLS.FORECAST_INGEST_BATCHES}/${id}`);
export const getIngestBatchErrors     = (id)     => get (`${FORECAST_URLS.FORECAST_INGEST_BATCHES}/${id}/errors`);
export const pullDboPayments          = (body)   => post(FORECAST_URLS.FORECAST_INGEST_PULL_DBO, body);
export const validateIngestBatch      = (id)     => post(`${FORECAST_URLS.FORECAST_INGEST_VALIDATE}/${id}`);
export const promoteIngestBatch       = (id)     => post(`${FORECAST_URLS.FORECAST_INGEST_PROMOTE}/${id}`);


// ═════════════════════════════════════════════════════════════════
//   RECOMPUTE
// ═════════════════════════════════════════════════════════════════
export const recomputeBehavioralAttributes = () => post(FORECAST_URLS.FORECAST_RECOMPUTE_BEHAVIORAL);
export const recomputePtpKeepRates         = () => post(FORECAST_URLS.FORECAST_RECOMPUTE_KEEP_RATES);
export const recomputePersonaAssignments   = () => post(FORECAST_URLS.FORECAST_RECOMPUTE_ASSIGNMENTS);

// ═════════════════════════════════════════════════════════════════
//   COMPARE
// ═════════════════════════════════════════════════════════════════
export const getBaselineCompare = (modelIds, startDate, endDate) =>
    get(FORECAST_URLS.FORECAST_COMPARE_BASELINE, {
      modelIds: modelIds.join(","),
      startDate,
      endDate,
    });
// ═════════════════════════════════════════════════════════════════
//   CALENDAR FACTORS
// ═════════════════════════════════════════════════════════════════
export const getCalendarFactors  = ()     => get(FORECAST_URLS.FORECAST_CALENDAR_FACTORS);
export const saveCalendarFactors = (body) => put(FORECAST_URLS.FORECAST_CALENDAR_FACTORS, body);